import {
  ComboboxLikeRenderOptionInput,
  ComboboxItem,
  Group,
  Text,
  Box,
} from "@mantine/core"
import { IconComponents, IconHanger, IconShoe } from "@tabler/icons-react"
import { Select } from "@tfg/ui"
import { t } from "i18next"
import { Dispatch, SetStateAction } from "react"

import { TraceabilityResponses } from "@tfg/types/endpoints/traceability"
import { getTraceabilityRequestsQueryParams } from "@tfg/types/schemas/traceabilityRequests"

import { IconRawMaterial } from "~/components/icons/activities/IconRawMaterial"
import { useGetTraceabilityRequestsTypes } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useGetTraceabilityRequestsTypes"
import { getTranslation } from "~/utils/translations"

type Props = {
  filtersValue: getTraceabilityRequestsQueryParams["filters"]
  setFiltersValue: Dispatch<
    SetStateAction<getTraceabilityRequestsQueryParams["filters"]>
  >
}

const renderTypeIcon = (
  itemType: TraceabilityResponses.TraceabilityRequestItem
) => {
  const type = itemType.type
  if (type === "product") {
    return type.toLowerCase().includes("footwear") ? (
      <IconShoe size={16} />
    ) : (
      <IconHanger size={16} />
    )
  }
  if (type === "component") return <IconComponents size={16} />
  if (type === "material") return <IconRawMaterial size={16} />
  return null
}

const getItemTypeTranslation = (
  type: TraceabilityResponses.TraceabilityRequestItem["type"]
) => {
  switch (type) {
    case "product":
      return t("traceability:itemType.product")
    case "component":
      return t("traceability:itemType.component")
    case "material":
      return t("traceability:itemType.material")
  }
}

export const TypesFilter = ({ filtersValue, setFiltersValue }: Props) => {
  const { data, isLoading } = useGetTraceabilityRequestsTypes({
    filters: filtersValue,
  })

  const TYPE_OPTIONS =
    data?.map((item) => ({
      label: `${t(getItemTypeTranslation(item.type))}-${item.typeTextContent ? getTranslation(item.typeTextContent) : ""}`,
      value: `${item.typeTextContent?.originalText}`,
      item,
    })) ?? []

  return (
    <Select
      isLoading={isLoading}
      placeholder={t("traceability:filters.type")}
      value={filtersValue?.type ?? null}
      onChange={(val) =>
        setFiltersValue((prev) => ({ ...prev, type: val ?? undefined }))
      }
      data={TYPE_OPTIONS}
      w={180}
      clearable
      customRender={({
        option,
        checked,
      }: ComboboxLikeRenderOptionInput<
        ComboboxItem & {
          item?: TraceabilityResponses.TraceabilityRequestItem
        }
      >) => {
        const { item } = option
        if (!item) return null
        return (
          <Group
            data-checked={checked}
            py="var(--tfg-molecule-padding-y)"
            px="var(--tfg-molecule-padding-x)"
            gap="var(--tfg-molecule-padding-y)"
            wrap="nowrap"
          >
            <Box miw={16}>{renderTypeIcon(item)}</Box>
            <Text>{option.label}</Text>
          </Group>
        )
      }}
    />
  )
}
